import { format, parse } from 'date-fns';

import { API } from '../API';

// Analysis-window boundary conversions: the API takes whole days as yyyy-MM-dd
// and echoes datetimes back — only the calendar day is meaningful.
export const toApiDate = (value: Date | null | undefined): string | undefined =>
  value ? format(value, 'yyyy-MM-dd') : undefined;

export const parseApiDate = (value: string | null | undefined): Date | null =>
  value ? parse(value.slice(0, 10), 'yyyy-MM-dd', new Date()) : null;

export interface NodeTagDTO {
  tag: string;
  name_en: string;
  name_uk: string;
}

export interface SocialGraphNodeDTO {
  id: number;
  name: string;
  platform: string;
  url: string | null;
  in_scope: boolean;
  in_strength: number;
  out_strength: number;
  degree: number;
  pagerank: number;
  betweenness: number;
  community: number;
  hub_score: number;
  authority_score: number;
  actor_role: string;
  reciprocity: number | null;
  clustering_coeff: number | null;
  platform_diversity: number | null;
  bridge_score: number | null;
  weight_gini: number | null;
  avg_z_score: number | null;
  weighted_anomaly_score: number | null;
  is_high_risk: boolean;
  discreditations: string[];
  tags: NodeTagDTO[];
  // Added by backend PR #823 (not yet released) — same shape the case graph
  // returns. Optional so graphs stored before the release keep parsing; the UI
  // must treat absence as "no data", not "clean".
  affiliations?: CaseAffiliationDTO[];
  // SUSPENDED | INAUTHENTIC_BEHAVIOR | IMMATURE_ACCOUNT — at most one under the
  // current rules (bucketed by priority, mirroring the narrative widgets), but
  // the contract is a list. Also PR #823.
  inauthentic_types?: string[];
}

export interface SocialGraphEdgeDTO {
  source: number;
  target: number;
  weight: number;
  total_count: number;
  z_score: number;
  mention_types: Record<string, number> | null;
  time_buckets: Record<string, number> | null;
}

export interface SocialGraphClusterDTO {
  cluster_id: number;
  nodes: number;
  edges: number | null;
  density: number | null;
  edges_internal: number | null;
  edges_external: number | null;
  internal_weight: number | null;
  external_weight: number | null;
  homophily: number | null;
  conductance: number | null;
}

export interface SocialGraphGlobalMetrics {
  nodes_count: number;
  edges_count: number;
  density: number;
  reciprocity: number;
  total_actors_in_scope: number;
  // why scope actors are missing from the graph (backend PR #935): no
  // relations / dropped by the backend's own pruning vs. squeezed out by the
  // node cap. Absent (→ 0) on graphs saved before the PR until regenerated
  actors_without_interactions?: number;
  actors_over_limit?: number;
}

export interface PrecomputedLayoutDTO {
  mode: string;
  nodes: Record<string, [number, number, boolean]>;
}

export interface SocialGraphAnalyzeResponse {
  mode: 'zscore' | 'exploratory';
  nodes: SocialGraphNodeDTO[];
  edges: SocialGraphEdgeDTO[];
  clusters: SocialGraphClusterDTO[];
  // full node copies grouped by role — the page never reads it (group members
  // come from graph attributes) and it roughly doubles the payload, so the
  // backend may stop serialising it (scaling plan 1.7); never depend on it
  roles_summary?: Record<string, SocialGraphNodeDTO[]>;
  global_metrics: SocialGraphGlobalMetrics;
  layout?: PrecomputedLayoutDTO;
}

// Present only on single-actor explore graphs: the sampling knobs plus what they
// actually produced. `neighbour_limit` is the cap that was asked for,
// `neighbours_returned` how many qualified, and `truncated` whether the cap cut
// anyone off — so a "showing the N strongest connections" hint stays honest.
export interface ExploreMetaDTO {
  actor_id: number;
  min_interactions: number;
  neighbour_limit: number;
  edge_min_interactions: number;
  neighbours_returned: number;
  truncated: boolean;
}

export interface SocialGraphModesResponse {
  exploratory: SocialGraphAnalyzeResponse;
  // explore graphs are exploratory-only — their zscore payload is an empty shell;
  // while ZSCORE_ENABLED is off the backend may omit the mode entirely
  // (scaling plan 1.7: it is the other half of the download)
  zscore?: SocialGraphAnalyzeResponse;
  explore?: ExploreMetaDTO | null;
}

export interface ActorGraphRequest {
  actor_ids?: number[];
  actor_group_ids?: number[];
  min_interactions?: number;
  louvain_resolution?: number;
  // Optional custom name; blank/omitted → backend derives one from the source.
  name?: string;
  // Optional analysis window, yyyy-MM-dd; both bounds are whole UTC days, inclusive.
  start_date?: string;
  end_date?: string;
}

// Single-actor explore graph: the actor plus their strongest neighbours and the
// links between those neighbours. Exploratory-only — no zscore mode, no report.
export interface ExploreActorRequest {
  actor_id: number;
  // neighbour qualification: pair strength (both directions summed); default 3
  min_interactions?: number;
  // how many of the strongest neighbours to keep (1–200); default 50
  neighbour_limit?: number;
  // a link between two neighbours must reach this to be drawn; default 2
  edge_min_interactions?: number;
  // blank/omitted → backend falls back to the actor's name
  name?: string;
  // same whole-UTC-day window semantics as ActorGraphRequest
  start_date?: string;
  end_date?: string;
}

export interface AsyncJobDTO {
  task_id: string;
  status: string;
}

export interface ActorGraphResultDTO {
  task_id: string;
  status: string;
  result: SocialGraphModesResponse | null;
  error: string | null;
  error_detail?: string | null;
}

export interface CaseAffiliationDTO {
  entity_type: string;
  country: string | null;
}

export interface CaseNodeDTO {
  id: number;
  name: string;
  platform: string;
  url: string | null;
  role: string;
  community: number;
  in_strength: number;
  out_strength: number;
  degree: number;
  betweenness: number;
  is_high_risk: boolean;
  discreditations: string[];
  is_state_affiliated: boolean;
  affiliations: CaseAffiliationDTO[];
  origin_country: string | null;
  audience: number | null;
  is_verified: boolean | null;
}

export interface CaseEdgeDTO {
  // producer = original author (message.origin_actor_id), amplifier = the
  // account that reposted it (message.actor_id). The frontend draws the edge
  // amplifier → producer so the source is the acting actor, as in actor graphs.
  producer: number;
  amplifier: number;
  weight: number;
  time_buckets: Record<string, number> | null;
}

export interface CaseClusterDTO {
  cluster_id: number;
  nodes: number;
  hra_count: number;
  hra_share: number;
  state_affiliated_count: number;
  state_affiliated_share: number;
}

export interface CaseGraphGlobalMetrics {
  nodes_count: number;
  edges_count: number;
  producers_count: number;
  amplifiers_count: number;
}

export interface CaseGraphResponse {
  nodes: CaseNodeDTO[];
  edges: CaseEdgeDTO[];
  clusters: CaseClusterDTO[];
  global_metrics: CaseGraphGlobalMetrics;
  layout?: PrecomputedLayoutDTO;
}

export interface CaseGraphResultDTO {
  task_id: string;
  status: string;
  result: CaseGraphResponse | null;
  error: string | null;
  error_detail?: string | null;
}

export type SavedGraphStatus = 'queued' | 'generating' | 'ready' | 'failed';
// 'threat' is a case-graph source too (kind stays 'case', same repost engine) —
// it just scopes the query to a threat instead of a narrative/event.
// 'actor' is a single-actor explore graph (enqueued via /explore-actor); a
// migration backfilled old actor rows, so the label is reliable in lookups.
export type SavedGraphSourceType = 'actor_group' | 'actor' | 'case' | 'topic' | 'threat';
// Which engine produced the graph — decides the stored result shape and which
// task endpoints cancellation goes through.
export type SavedGraphKind = 'actor' | 'case';

export interface SavedGraphUserDTO {
  id: number;
  first_name: string | null;
  last_name: string | null;
}

export interface SavedGraphDTO {
  id: number;
  name: string;
  kind: SavedGraphKind;
  source_type: SavedGraphSourceType | null;
  source_id: number | null;
  status: SavedGraphStatus;
  created_at: string;
  // Celery task id while queued/generating (null otherwise) — cancellation reuses the
  // existing DELETE task endpoints (cancelActors/cancelCase), no graph-level cancel
  task_id: string | null;
  user: SavedGraphUserDTO | null;
  // The period the graph covers (actor kind carries the analysis window, case
  // graphs echo the case's own period); null on both means the whole history.
  // A windowless actor graph gets its dates with its first successful run, and
  // while a regenerate is in flight they keep describing the previous result.
  start_date?: string | null;
  end_date?: string | null;
  // Ready-state summary of the stored result (scaling plan 1.7): once the list
  // endpoint carries these, the widget shows its four numbers without
  // downloading the whole result. Absent on the deployed backend → the widget
  // falls back to fetching the detail.
  nodes_count?: number | null;
  edges_count?: number | null;
  communities_count?: number | null;
  bridging_actors_count?: number | null;
}

export interface SavedGraphListDTO {
  objects: SavedGraphDTO[];
  page: number;
  size: number;
  total: number;
}

// `result` is the last successful run's payload — present even while a regeneration
// is in flight, so the previous graph stays viewable. Shape depends on `kind`.
export interface SavedGraphDetailDTO extends SavedGraphDTO {
  result: SocialGraphModesResponse | CaseGraphResponse | null;
  error: string | null;
  error_detail?: string | null;
}

// PDF Z-score report of a saved actor graph (backend PR #765). One report per
// graph: requesting while queued/generating/ready returns the existing row; a
// failed one — or a ready one in another language — is replaced by a fresh
// generation. Actor (Z-score) graphs only: case/topic graphs get 400.
export type SocialGraphReportStatus = 'queued' | 'generating' | 'ready' | 'failed';
export type SocialGraphReportLang = 'UA' | 'EN';

export interface SocialGraphReportDTO {
  id: number;
  graph_id: number;
  status: SocialGraphReportStatus;
  lang: SocialGraphReportLang;
  error: string | null;
  error_detail?: string | null;
  created_at: string;
}

export const socialGraphApi = {
  async listGraphs(
    params: {
      page?: number;
      size?: number;
      // both together narrow the list to graphs built from that source — a
      // case/group/actor page asking for its own graphs
      source_type?: SavedGraphSourceType;
      source_id?: number;
    },
    signal?: AbortSignal,
  ): Promise<SavedGraphListDTO> {
    const urlParams = new URLSearchParams();
    if (params.page !== undefined) {
      urlParams.set('page', String(params.page));
    }
    if (params.size !== undefined) {
      urlParams.set('size', String(params.size));
    }
    if (params.source_type !== undefined) {
      urlParams.set('source_type', params.source_type);
    }
    if (params.source_id !== undefined) {
      urlParams.set('source_id', String(params.source_id));
    }
    return API.fetch('GET', `/API/v2/social-graph/graphs?${urlParams.toString()}`, null, null, signal);
  },

  async deleteGraph(id: number): Promise<void> {
    return API.fetch('DELETE', `/API/v2/social-graph/graphs/${id}`);
  },

  // Optional body: the analysis window is replaced only when a date field is
  // explicitly present (explicit nulls clear it back to the whole history; a
  // body without date fields keeps the stored window), and a non-blank `name`
  // renames the graph in the same call. Only actor graphs carry a window —
  // sending date fields for a case graph is a 400, so callers must gate them
  // on the graph's kind.
  async regenerateGraph(
    id: number,
    params?: { start_date?: string | null; end_date?: string | null; name?: string },
  ): Promise<SavedGraphDTO> {
    return API.fetch('POST', `/API/v2/social-graph/graphs/${id}/regenerate`, null, params ?? null);
  },

  async renameGraph(id: number, name: string): Promise<SavedGraphDTO> {
    return API.fetch('PATCH', `/API/v2/social-graph/graphs/${id}`, null, { name });
  },

  async getGraph(id: number, signal?: AbortSignal): Promise<SavedGraphDetailDTO> {
    return API.fetch('GET', `/API/v2/social-graph/graphs/${id}`, null, null, signal);
  },

  async requestReport(graphId: number, lang: SocialGraphReportLang): Promise<SocialGraphReportDTO> {
    return API.fetch('POST', `/API/v2/social-graph/graphs/${graphId}/report`, null, { lang });
  },

  async getReport(graphId: number, signal?: AbortSignal): Promise<SocialGraphReportDTO> {
    return API.fetch('GET', `/API/v2/social-graph/graphs/${graphId}/report`, null, null, signal);
  },

  // short-lived presigned URL for the generated PDF (400 while not ready)
  async getReportDownloadLink(graphId: number): Promise<string> {
    return API.fetch('GET', `/API/v2/social-graph/graphs/${graphId}/report/download`);
  },

  async enqueueActors(
    params: ActorGraphRequest,
    signal?: AbortSignal,
  ): Promise<AsyncJobDTO> {
    return API.fetch('POST', '/API/v2/social-graph/analyze-actors', null, params, signal);
  },

  async pollActors(taskId: string, signal?: AbortSignal): Promise<ActorGraphResultDTO> {
    return API.fetch('GET', `/API/v2/social-graph/analyze-actors/${taskId}`, null, null, signal);
  },

  // Returns the same task handle as enqueueActors — poll/cancel via the
  // analyze-actors task endpoints. Saved rows come back with
  // source_type 'actor' + source_id = actor_id.
  async enqueueExploreActor(
    params: ExploreActorRequest,
    signal?: AbortSignal,
  ): Promise<AsyncJobDTO> {
    return API.fetch('POST', '/API/v2/social-graph/explore-actor', null, params, signal);
  },

  async enqueueCase(
    params: {
      event_id?: number;
      narrative_id?: number;
      threat_id?: number;
      louvain_resolution?: number;
      // Optional custom name; blank/omitted → the scope's own name.
      name?: string;
    },
    signal?: AbortSignal,
  ): Promise<AsyncJobDTO> {
    return API.fetch('POST', '/API/v2/social-graph/case', null, params, signal);
  },

  async pollCase(taskId: string, signal?: AbortSignal): Promise<CaseGraphResultDTO> {
    return API.fetch('GET', `/API/v2/social-graph/case/${taskId}`, null, null, signal);
  },

  // Cancel a queued/running generation task. The DELETE endpoints share the poll
  // paths; the right one is picked by the saved graph's `kind` (actor vs case).
  // Cancelling a first run deletes the saved graph; cancelling a regeneration
  // reverts it to ready with the previous result.
  async cancelActors(taskId: string): Promise<AsyncJobDTO> {
    return API.fetch('DELETE', `/API/v2/social-graph/analyze-actors/${taskId}`);
  },

  async cancelCase(taskId: string): Promise<AsyncJobDTO> {
    return API.fetch('DELETE', `/API/v2/social-graph/case/${taskId}`);
  },
};
